import { runOnCustomEvent, runWhenAttributionIsSet } from '../../helpers';
import { DIALOGS_READY_EVENT_NAME } from './hcp-dialog.constants';

const DESKTOP_SCREEN_WIDTH_THRESHOLD = 1210;
const MARKETING_CHANNEL_ORGANIC_DIRECT = 'OrganicDirect';
const COMPONENT_NAME = 'known-pro-popup';
const POPUP_CLOSED_EVENT_NAME = `${COMPONENT_NAME}-closed`;
const POPUP_DISMISSED_KEY = 'known_pro_popup_dismissed';

/**
 * Controls the Known Pro dialog behavior, determining when and how it should be shown.
 */
class HCPKnownProDialog {
	/**
	 * Underlying dialog element enhanced by `HCPDialog`.
	 *
	 * @type {HTMLElement & { show: () => void, close: () => void }}
	 */
	#dialog;
	#loginAnchor;
	#yesBtn;
	#noBtn;
	#opened;

	/**
	 * Creates a new Known Pro dialog controller.
	 *
	 * @param {HTMLElement & { show: () => void, close: () => void }} dialog - Dialog element configured by `HCPDialog`.
	 */
	constructor(dialog) {
		this.#dialog = dialog;
		this.#loginAnchor = dialog.querySelector('a');
		this.#yesBtn = dialog.querySelector('.yes-btn');
		this.#noBtn = dialog.querySelector('.no-btn');
		this.#opened = false;
		this.#init();
	}

	/**
	 * Checks if the current viewport qualifies as desktop.
	 *
	 * @private
	 * @returns {boolean} True when the viewport width meets the desktop threshold.
	 */
	#isDesktop() {
		return window.matchMedia(`(min-width: ${DESKTOP_SCREEN_WIDTH_THRESHOLD}px)`).matches;
	}

	/**
	 * Determines whether at least 24 hours have passed since the dialog was last dismissed.
	 *
	 * @private
	 * @returns {boolean} True when the dialog has not been dismissed in the last 24 hours.
	 */
	#hasBeen24HoursSinceLastShown() {
		const yesterday = (new Date().getTime() - 24 * 3600 * 1000).toString();
		const lastDismissed = sessionStorage.getItem(POPUP_DISMISSED_KEY);
		if ([undefined, null, '', 'null'].includes(lastDismissed)) {
			return true;
		}

		return lastDismissed < yesterday;
	}

	/**
	 * Resolves when the user's attribution data indicates the dialog should be displayed.
	 *
	 * @private
	 * @returns {Promise<boolean>} Promise resolving to true when marketing channel and authentication match requirements.
	 */
	async #attributesAlignForPopup() {
		return new Promise((resolve) => {
			runWhenAttributionIsSet(() => {
				const data = window.identityGraph;

				const attributesAlignForPopup =
					data.marketing_channel === MARKETING_CHANNEL_ORGANIC_DIRECT && data.pro?.is_authenticated === true;
				resolve(attributesAlignForPopup);
			});
		});
	}

	/**
	 * Determines whether the dialog should be shown, based on viewport, timing, and attribution.
	 *
	 * @private
	 * @returns {Promise<boolean>} Promise resolving to true when the dialog should be shown.
	 */
	async #doesItNeedToBeShown() {
		const attributesAlignForPopup = await this.#attributesAlignForPopup();
		return this.#isDesktop() && this.#hasBeen24HoursSinceLastShown() && attributesAlignForPopup;
	}

	/**
	 * Displays the dialog by invoking its `show` method.
	 *
	 * @private
	 * @returns {void}
	 */
	#showDialog(withTriggerEvent = true) {
		this.#dialog.show(withTriggerEvent);
		this.#opened = true;
	}

	/**
	 * Closes the dialog by invoking its `close` method.
	 *
	 * @private
	 * @returns {void}
	 */
	#closeDialog(withTriggerEvent = true) {
		this.#dialog.close(withTriggerEvent);
		this.#opened = false;
	}

	/**
	 * Determines if the current viewport is desktop and the popup is hidden.
	 *
	 * @private
	 * @returns {boolean} True if on desktop and the dialog is not open.
	 */
	#isDesktopAndPopupIsHidden() {
		return this.#isDesktop() && !this.#opened;
	}

	/**
	 * Determines if the current viewport is not desktop and the popup is visible.
	 *
	 * @private
	 * @returns {boolean} True if not on desktop and the dialog is open.
	 */
	#isNotDesktopAndPopupIsVisible() {
		return !this.#isDesktop() && this.#opened;
	}

	/**
	 * Handles the resize event to ensure dialog state matches the device type.
	 *
	 * When resizing between desktop and non-desktop, the dialog will toggle
	 * open or closed to match expected UX behavior.
	 *
	 * @private
	 * @returns {void}
	 */
	#resizeHandler() {
		if(this.#isDesktopAndPopupIsHidden() && this.#doesItNeedToBeShown()) {
			this.#showDialog(true);
		}

		if(this.#isNotDesktopAndPopupIsVisible()) {
			this.#closeDialog(false);
		}
	}

	/**
	 * Sets up dismissal tracking when the dialog is closed.
	 *
	 * @private
	 * @returns {void}
	 */
	#setDialogEvents() {
		runOnCustomEvent(POPUP_CLOSED_EVENT_NAME, () => {
			const date = new Date().getTime().toString();
			sessionStorage.setItem(POPUP_DISMISSED_KEY, date);
		});

		this.#yesBtn.addEventListener('click', () => {
			window.location.href = this.#loginAnchor.href;
		});

		this.#noBtn.addEventListener('click', () => {
			this.#closeDialog();
		});

		window.addEventListener('resize', this.#resizeHandler.bind(this));
	}

	/**
	 * Initializes the dialog workflow and, when needed, registers events before showing the dialog.
	 *
	 * @private
	 * @returns {void}
	 */
	async #init() {
		if (await this.#doesItNeedToBeShown()) {
			this.#setDialogEvents();
			this.#showDialog();
		} else {
		}
	}
}

/**
 * Initializes the Known Pro dialog if the dialog element exists in the DOM.
 *
 * This function looks for an element with ID 'known-pro-popup'. If found,
 * it creates an instance of HCPKnownProDialog to manage its behavior.
 *
 * @returns {void}
 */
function initKnownProDialog() {
	const dialog = document.getElementById('known-pro-popup');

	if (dialog) {
		const knownProDialog = new HCPKnownProDialog(dialog);
	}
}

/**
 * Initializes the Known Pro dialog logic when the hcp-dialogs are initialized.
 *
 * This function listens for the 'hcp-dialogs-ready' event and initializes the Known Pro dialog logic when the hcp-dialogs are initialized.
 *
 * @returns {void}
 */
runOnCustomEvent(
	DIALOGS_READY_EVENT_NAME,
	() => {
		initKnownProDialog();
	},
	window,
	!!window.hcpDialogsReady
);
